// Client documents in the browser: what can be uploaded, and how data read
// from a PDF (Constancia de Situación Fiscal, constancia de la CURP) is merged
// into a client form without overwriting what the user already typed.
import {
  DOCUMENT_CONTENT_TYPES,
  DOSSIER_FIELD_LABELS,
  type DocumentType,
  type ExtractedClientFields,
  MAX_DOCUMENT_BYTES,
} from "@pcj/shared";

export const DOCUMENT_ACCEPT = DOCUMENT_CONTENT_TYPES.join(",");

/** Kinds of document whose PDF may carry readable client data. */
export const READABLE_DOCUMENT_TYPES: readonly DocumentType[] = ["tax_certificate", "curp"];

/** Why a file cannot be uploaded, or null. The API checks again, from the file's bytes. */
export function fileProblem(file: File): string | null {
  if (file.size === 0) return "El archivo está vacío.";
  if (file.size > MAX_DOCUMENT_BYTES) return `El archivo pesa más de ${MAX_DOCUMENT_BYTES / 1024 / 1024} MB.`;
  if (file.type && !(DOCUMENT_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return "Formato no admitido. Sube un PDF o una imagen JPG, PNG o WEBP.";
  }
  return null;
}

export const isPdf = (file: File) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

/** Text fields of the client form that a document can fill. */
export const EXTRACTABLE_FIELDS = [
  "name",
  "rfc",
  "curp",
  "birthDate",
  "incorporationDate",
  "address",
  "phone",
  "country",
] as const satisfies readonly (keyof ExtractedClientFields)[];

export type ExtractableField = (typeof EXTRACTABLE_FIELDS)[number];

export const extractedFieldLabel = (field: ExtractableField) => DOSSIER_FIELD_LABELS[field];

const comparable = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[\s.,]+/g, " ")
    .trim()
    .toUpperCase();

export interface FieldMerge {
  /** Empty fields that take the document's value. */
  fill: Partial<Record<ExtractableField, string>>;
  /** Fields that already hold a different value: the user decides. */
  conflicts: { field: ExtractableField; value: string; current: string }[];
}

/** How the data of a document fits into the current values of a client. */
export function mergeExtracted(
  current: Record<ExtractableField, string | null | undefined>,
  extracted: ExtractedClientFields,
): FieldMerge {
  const merge: FieldMerge = { fill: {}, conflicts: [] };
  for (const field of EXTRACTABLE_FIELDS) {
    const value = extracted[field];
    if (!value) continue;
    const existing = current[field]?.trim() ?? "";
    if (existing === "") merge.fill[field] = value;
    else if (comparable(existing) !== comparable(value)) merge.conflicts.push({ field, value, current: existing });
  }
  return merge;
}
